import React, { useCallback, useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence, MotionConfig, useInView, useReducedMotion } from 'framer-motion';
import useEmblaCarousel from 'embla-carousel-react';
import NumberFlow from '@number-flow/react';
import {
  ArrowRight, ArrowDown, ArrowUpRight, Check, ChevronDown, ShieldCheck, Gift, Menu, MessageCircle, Play, Users,
} from 'lucide-react';
import LeadCheckoutDialog, { CheckoutPlan } from '@/components/LeadCheckoutDialog';
import ChatWidget from '@/components/ChatWidget';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Sheet, SheetClose, SheetContent, SheetTitle, SheetTrigger } from '@/components/ui/sheet';
import { AMAMENTACAO_DE_OURO, AMAMENTACAO_NA_PRATICA, COMUNIDADE, DORES_E_LESOES, HOME_CAMPAIGN, ROSANE_STATS } from '@/data/offers';
import type { Campaign } from '@/data/offers';
import { captureFirstTouch, fetchGeo } from '@/lib/leadTracking';

const PRIVACY_LINK = '/termos-e-politicas#privacidade';
const WHATSAPP_LINK = 'https://wa.me/5551983007447?text=Ol%C3%A1%2C%20acessei%20o%20site%20e%20tenho%20uma%20d%C3%BAvida%20sobre%20o%20curso%20Amamenta%C3%A7%C3%A3o%20na%20Pr%C3%A1tica.';
const EASE_OUT = [0.23, 1, 0.32, 1] as const;

// ─── Conteúdo ──────────────────────────────────────────────────────────────

const PPS_STEPS = [
  {
    key: 'posicao',
    letter: 'P',
    name: 'Posição',
    text: 'Tudo começa pelo corpo do bebê. Ele fica virado para você, barriga com barriga, com orelha, ombro e quadril na mesma linha. O nariz chega na altura do mamilo, e é o bebê que vai até a mama, não a mama até ele. Sem essa base, a pega certa não acontece e a dor aparece logo nas primeiras mamadas.',
    observe: ['Barriga do bebê encostada na sua', 'Cabeça e corpo alinhados, sem pescoço torcido', 'Você apoiada, sem se curvar para a frente'],
    image: '/lovable-uploads/mae_amamentando_bebe.png',
    alt: 'Mãe sentada na poltrona amamentando o bebê bem posicionado',
  },
  {
    key: 'pega',
    letter: 'P',
    name: 'Pega',
    text: 'Uma boa pega vai muito além da boca de peixinho. O bebê abre bem a boca e abocanha boa parte da aréola, não só o mamilo. É nesse momento que a mamada fica confortável ou começa a machucar, e são quatro sinais simples que mostram de que lado você está.',
    observe: ['Boca bem aberta', 'Lábio de baixo virado para fora', 'Queixo encostado na mama', 'Mais aréola aparecendo em cima do que embaixo'],
    image: '/lovable-uploads/ad697021-e3dd-433a-9eea-c5c708842c55.png',
    alt: 'Bebê mamando com a boca bem aberta na aréola',
  },
  {
    key: 'succao',
    letter: 'S',
    name: 'Sucção',
    text: 'Com a pega certa, o bebê cria um vácuo dentro da boca, e é esse vácuo que tira o leite. A mamada ganha ritmo, você escuta o bebê engolir e o seu corpo recebe o sinal para produzir mais. Quanto mais eficiente a sucção, mais o bebê mama, mais ganha peso e mais leite você tem.',
    observe: ['Sugadas longas e ritmadas, com pausas', 'Barulho de engolir, sem estalos', 'Bochechas redondas, sem covinhas'],
    image: '/lovable-uploads/4d147df6-6ec4-42a7-a733-47205740cb1c.png',
    alt: 'Rosane demonstrando a sucção com um modelo de mama',
  },
];

const TESTIMONIALS = [
  { id: '12-2', caption: 'Fez o curso ainda grávida' },
  { id: '12-6', caption: 'Gabriela, de 2,5 kg para 4,1 kg em 45 dias' },
  { id: '12-3', caption: 'Bruna, fissura e dor resolvidas' },
  { id: '12-1', caption: 'Família Mesquitta' },
  { id: '12-4', caption: 'Rebeca, disfunção oral e refluxo' },
  { id: '12-5', caption: '@alinegalle, pega certa ainda no hospital' },
  { id: '12-7', caption: 'Fernanda, amamentação exclusiva depois da UTI' },
  { id: '27-1', caption: '@starsoares, recado para quem está grávida' },
  { id: '27-2', caption: 'Avaliações no Google' },
  { id: '27-3', caption: 'Cecília, depois do ajuste de posição e pega' },
  { id: '27-4', caption: 'Preparo antes do parto, sem fissura' },
  { id: '27-6', caption: 'Isa, 3 anos e 4 meses mamando' },
  { id: '27-5', caption: 'Bianca Schorn, dermatologista' },
  { id: '27-8', caption: 'Mariane, relato de aluna' },
  { id: '27-9', caption: 'Resolvido com consultoria a distância' },
  { id: '27-10', caption: 'Recados de alunas nos stories' },
  { id: '27-11', caption: '"Meu curso é meu Google"' },
  { id: '27-7', caption: 'Aprendeu com o conteúdo gratuito' },
];
// Duas fileiras sem repetir imagem: metade em cima, metade embaixo
const ROW_TOP = TESTIMONIALS.filter((_, i) => i % 2 === 0);
const ROW_BOTTOM = TESTIMONIALS.filter((_, i) => i % 2 === 1);

const PHASES = [
  {
    name: 'Preparo',
    range: 'Módulos 1 a 3',
    modules: [
      { n: 1, title: 'O que influencia a amamentação', desc: 'Como a mama funciona, o emocional do puerpério e a sua rede de apoio.', topics: ['A amamentação como aprendizado da dupla', 'Benefícios reais do aleitamento', 'Anatomia da mama e dos mamilos', 'O emocional no puerpério', 'Como montar a sua rede de apoio'] },
      { n: 2, title: 'Método PPS: posição, pega e sucção', desc: 'O coração do curso, passo a passo, com mães e bebês reais.', topics: ['Posição da mãe e do bebê em diferentes situações', 'Como amolecer a aréola antes da mamada', 'Técnica do sanduíche', 'Pega correta passo a passo', 'Como perceber e ajustar uma pega errada', 'Sucção efetiva na prática'] },
      { n: 3, title: 'A rotina de verdade', desc: 'Primeiras horas, descida do leite, sinais de fome e as noites.', topics: ['As primeiras horas de vida', 'Apojadura, a descida do leite', 'Sinais de fome', 'Como acalmar o bebê para mamar', 'Livre demanda na prática', 'Amamentação noturna e de gêmeos', 'A "hora da bruxa"'] },
    ],
  },
  {
    name: 'Manejo',
    range: 'Módulos 4 a 6',
    modules: [
      { n: 4, title: 'Situações específicas', desc: 'Cada corpo e cada bebê pedem um ajuste diferente.', topics: ['Mamas grandes, pendulares, com prótese ou após mastectomia', 'Mamilos planos e invertidos', 'Bebês prematuros, PIG e GIG', 'Cólica, icterícia e refluxo', 'Alergia à proteína do leite de vaca', 'Baixo ganho de peso'] },
      { n: 5, title: 'Dificuldades na amamentação', desc: 'Como reconhecer cada problema cedo e agir com técnica.', topics: ['Atraso na descida do leite', 'Dor e lesões no mamilo', 'Leite empedrado e ductos bloqueados', 'Mastite e abscesso', 'Candidíase, Raynaud e DMER', 'Muito leite, pouco leite e recusa do peito'] },
      { n: 6, title: 'Técnicas práticas', desc: 'Tudo demonstrado para você fazer sozinha, em casa.', topics: ['Como ativar o reflexo de ejeção', 'Extração de alívio', 'Massagens e compressas', 'Compressão mamária', 'Translactação e relactação', 'Ordenha manual e como guardar o leite'] },
    ],
  },
  {
    name: 'Autonomia',
    range: 'Módulos 7 a 10',
    modules: [
      { n: 7, title: 'Produtos e equipamentos', desc: 'O que vale comprar e o que pode ficar na loja.', topics: ['Poltrona, conchas e pomadas', 'Absorventes e coletores', 'Bombas extratoras', 'Potes para guardar o leite', 'Chupeta e mamadeira com oferta pausada'] },
      { n: 8, title: 'Dúvidas da mãe que amamenta', desc: 'As perguntas que toda mãe faz e quase ninguém responde direito.', topics: ['Preparo das mamas na gestação', 'Alimentação de quem amamenta', 'Remédios e álcool', 'Anticoncepcional', 'Nova gravidez durante a amamentação', 'Saúde emocional'] },
      { n: 9, title: 'Cuidados com o recém-nascido', desc: 'O básico que dá segurança nos primeiros dias em casa.', topics: ['Necessidades do recém-nascido', 'Como fazer arrotar', 'Como desengasgar', 'Como perceber e aliviar cólicas', 'Exterogestação'] },
      { n: 10, title: 'Sono do bebê', desc: 'Como o sono se liga à amamentação.', topics: ['A relação entre sono e mamadas', 'Um método gentil para o bebê dormir', 'Sono sem atrapalhar a amamentação'] },
    ],
  },
];

const MOMENTS = [
  {
    key: 'gestante',
    label: 'Estou grávida',
    title: 'Chegue ao parto preparada',
    text: 'Chegue ao parto sabendo posicionar o bebê, reconhecer uma boa pega e o que esperar nos primeiros dias. Quem aprende antes chega mais confiante.',
    items: ['Quer chegar ao parto sabendo o que fazer', 'Quer saber o que comprar e o que não precisa', 'Tem mamilo plano, invertido ou prótese e quer saber como fica'],
  },
  {
    key: 'dificuldade',
    label: 'Sinto dor ou o bebê não pega',
    title: 'Resolva o que está acontecendo agora',
    text: 'Dor, fissura e bebê que solta o peito quase sempre têm a ver com posição e pega. Você aprende a corrigir já na próxima mamada.',
    items: ['Sente dor ou tem fissura a cada mamada', 'O bebê solta o peito, chora e você não sabe se é fome, cólica ou pega', 'Teve bebê prematuro, com baixo peso ou que passou pela UTI'],
  },
  {
    key: 'leite',
    label: 'Quero ter mais leite',
    title: 'Tenha leite suficiente para o seu bebê',
    text: 'A produção funciona por oferta e procura. Com sucção eficiente e livre demanda, o seu corpo entende que precisa produzir mais.',
    items: ['Tem medo de não ter leite suficiente', 'Quer amamentar só no peito até os 6 meses', 'Ouve uma opinião de cada pessoa e quer saber em quem confiar'],
  },
];

const HERO_CTAS = ['Quero me preparar', 'Quero amamentar sem dor', 'Quero produzir muito leite', 'Quero aprender o Método PPS'];

const STEPS_ACCESS = [
  { title: 'Você garante sua vaga', text: 'Pagamento seguro pela Hotmart, no Pix, boleto ou cartão em até 12x.' },
  { title: 'Recebe o acesso por e-mail', text: 'Logo depois da confirmação, os dados de acesso chegam no seu e-mail.' },
  { title: 'Assiste quando e onde quiser', text: 'No celular ou no computador, no seu ritmo, quantas vezes precisar.' },
];

const NEWS = [
  {
    source: 'PNAS',
    year: '2014',
    tag: 'Sucção',
    title: 'Ultrassom mostra que é o vácuo da sucção que tira o leite da mama',
    summary: 'Pesquisadores da Universidade de Tel Aviv analisaram imagens de bebês mamando e encerraram um debate de mais de cem anos: o leite sai pela pressão negativa dentro da boca, não por compressão. Pega e sucção andam juntas.',
    url: 'https://www.pnas.org/doi/10.1073/pnas.1319798111',
  },
  {
    source: 'Organização Mundial da Saúde',
    year: '2009',
    tag: 'Pega',
    title: 'OMS lista os 4 sinais de que o bebê está bem encaixado na mama',
    summary: 'Boca bem aberta, lábio de baixo virado para fora, queixo encostado na mama e mais aréola aparecendo acima do lábio de cima. Os quatro precisam estar presentes.',
    url: 'https://www.ncbi.nlm.nih.gov/books/NBK148965/',
  },
  {
    source: 'Revista da Escola de Enfermagem da USP',
    year: '2009',
    tag: 'Posição',
    title: 'Pescoço torcido e queixo longe da mama aparecem ligados à lesão no mamilo',
    summary: 'Estudo com 146 mães em um hospital universitário de São Paulo encontrou três sinais de posição e pega ligados às fissuras: pescoço do bebê torcido, queixo afastado e lábio de baixo virado para dentro.',
    url: 'https://revistas.usp.br/reeusp/article/view/40377',
  },
  {
    source: 'International Breastfeeding Journal',
    year: '2020',
    tag: 'Posição',
    title: 'Mudar a posição de amamentar reduziu fissuras e dor na alta da maternidade',
    summary: 'Em um ensaio com 208 gestantes na Itália, as mães que aprenderam a amamentar em posição reclinada saíram do hospital com menos problemas nas mamas, incluindo menos fissuras e menos dor no mamilo.',
    url: 'https://pmc.ncbi.nlm.nih.gov/articles/PMC7132959/',
  },
  {
    source: 'Birth',
    year: '1998',
    tag: 'Técnica',
    title: 'Na maioria das mães com problemas, a causa estava na técnica de sucção',
    summary: 'Estudo sueco comparou 52 duplas com dificuldades e 40 sem dificuldades. A técnica de mamar foi muito diferente entre os grupos, e corrigir o jeito de mamar era parte central do atendimento.',
    url: 'https://doi.org/10.1046/j.1523-536x.1998.00040.x',
  },
  {
    source: 'JAMA Pediatrics',
    year: '2015',
    tag: 'Benefícios',
    title: 'Amamentar por 6 meses ou mais foi associado a 19% menos risco de leucemia infantil',
    summary: 'Uma meta-análise de 18 estudos estimou que de 14% a 19% dos casos de leucemia na infância poderiam ser evitados com amamentação por seis meses ou mais.',
    url: 'https://doi.org/10.1001/jamapediatrics.2015.1025',
  },
];

const BONUSES = [
  { title: 'Protocolo AME', desc: 'Como tirar, guardar e oferecer o leite materno sem atrapalhar a amamentação.', from: 'R$ 97' },
  { title: 'Técnicas para o seu tipo de mama', desc: 'Orientações para mamas grandes, pequenas, caídas e mamilos planos ou invertidos.', from: 'R$ 147' },
  { title: 'Protocolo Enxoval', desc: 'O que você precisa comprar e o que pode deixar na loja.', from: 'R$ 97' },
  { title: 'Cuidados com o recém-nascido', desc: 'Como fazer arrotar, desengasgar, acalmar e aliviar cólicas.', from: 'R$ 67' },
  { title: 'Sono do bebê', desc: 'Como ajudar seu bebê a dormir melhor, sem deixar chorando.', from: 'R$ 197' },
];


// Perguntas escritas a partir do que as mães mais buscam no Google e nas IAs
const FAQ_GROUPS = [
  {
    title: 'Dúvidas sobre amamentação',
    items: [
      { q: 'É normal sentir dor ao amamentar?', a: 'Um leve desconforto nos primeiros segundos da mamada pode acontecer nos primeiros dias. Dor durante a mamada inteira, fissura ou sangramento não são normais e quase sempre indicam que a posição ou a pega precisam de ajuste. Corrigir a técnica costuma ser o que faz a dor passar.' },
      { q: 'Como me preparar para amamentar ainda na gravidez?', a: 'O que mais ajuda é aprender antes do parto: como a mama funciona, como posicionar o bebê, como reconhecer uma boa pega e o que é esperado nos primeiros dias. Saber a quem recorrer se surgir alguma dificuldade também faz diferença. Quem chega preparada sente mais segurança desde a primeira mamada.' },
      { q: 'Como saber se a pega do bebê está correta?', a: 'Segundo a Organização Mundial da Saúde, uma boa pega tem quatro sinais ao mesmo tempo: boca bem aberta, lábio de baixo virado para fora, queixo encostado na mama e mais aréola aparecendo em cima do que embaixo. Se a mamada dói, vale olhar esses quatro pontos primeiro.' },
      { q: 'O que fazer quando o mamilo racha ou tem fissura?', a: 'O primeiro passo é descobrir a causa, que na maioria das vezes está na pega. Ajustar posição e pega ajuda a lesão a cicatrizar sem interromper a amamentação. Procure um profissional se houver febre, vermelhidão forte na mama ou dor que não melhora.' },
      { q: 'Como saber se o meu bebê está mamando o suficiente?', a: 'Os melhores sinais são o ganho de peso nas consultas, várias fraldas molhadas por dia e o barulho do bebê engolindo durante a mamada. Bebê que dorme muito no peito, mama o tempo todo e não ganha peso merece avaliação da pega e da sucção.' },
      { q: 'Como saber se tenho pouco leite?', a: 'Mama mais mole e bebê querendo mamar com frequência são normais e não significam pouco leite. Os sinais que importam são do bebê: ganho de peso nas consultas, várias fraldas molhadas por dia e o barulho de engolir durante a mamada. Se esses sinais não aparecem, vale avaliar a pega e a sucção antes de pensar em complemento.' },
      { q: 'Como aumentar a produção de leite materno?', a: 'A produção funciona por oferta e procura: quanto mais o bebê mama com uma sucção eficiente, mais leite o corpo produz. Livre demanda, pega correta e esvaziar bem a mama são a base. Chás e receitas caseiras não substituem a técnica.' },
      { q: 'Leite empedrado: o que fazer?', a: 'O ingurgitamento melhora com mamadas frequentes, massagem suave e extração de alívio quando a mama está muito cheia para o bebê conseguir pegar. Febre, mama vermelha e quente ou mal-estar podem ser sinal de mastite e pedem avaliação profissional.' },
      { q: 'Dá para amamentar com mamilo plano ou invertido?', a: 'Dá. O bebê mama na aréola, não só no mamilo. Com algumas técnicas de preparo e ajustes de posição, mães com mamilo plano ou invertido conseguem amamentar sem dor.' },
      { q: 'Qual a melhor posição para amamentar um recém-nascido?', a: 'Não existe uma única posição certa. A melhor é aquela em que o bebê fica alinhado, barriga com barriga, e você fica confortável e apoiada. Deitada, sentada ou reclinada, o que importa é o alinhamento.' },
    ],
  },
  {
    title: 'Sobre o curso',
    items: [
      { q: 'O curso serve para quem ainda está grávida?', a: 'Serve para os dois momentos. Grávida, você chega ao parto sabendo o que observar. Se já é mãe, aprende a identificar e resolver o que está acontecendo agora.' },
      { q: 'Por quanto tempo tenho acesso?', a: 'O acesso é vitalício. Você assiste no seu ritmo e volta às aulas sempre que precisar, inclusive em uma próxima gestação.' },
      { q: 'Quais formas de pagamento são aceitas?', a: 'Pix, boleto e cartão de crédito em até 12x, pelo checkout seguro da Hotmart.' },
      { q: 'Tem garantia?', a: 'Tem. Você tem 7 dias depois da compra para assistir às aulas e decidir. Se não for para você, é só pedir o reembolso e devolvemos o valor inteiro.' },
      { q: 'Onde tiro minhas dúvidas durante o curso?', a: 'Você tem suporte especializado dentro da área de membros. Quem quiser contato direto com a Rosane pode entrar também na Comunidade Amamentação na Prática.' },
    ],
  },
];

const NAV_LINKS = [
  { href: '#metodo', label: 'O método' },
  { href: '#aula-gratuita', label: 'Aula grátis' },
  { href: '#comunidade', label: 'Comunidade' },
  { href: '#rosane', label: 'Rosane' },
  { href: '#duvidas', label: 'Dúvidas' },
];

const scrollTo = (id: string) => document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });

const primaryButton =
  'group inline-flex items-center justify-center gap-2 rounded-2xl bg-[#a54d9e] px-8 py-4 text-lg font-extrabold text-white shadow-[0_8px_20px_rgba(42,23,68,0.25)] transition-[background-color,transform] duration-150 ease-out hover:bg-[#8e3a87] active:scale-[0.97] focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-[#a54d9e]/30';

// ─── Peças ─────────────────────────────────────────────────────────────────

/** Fileira de depoimentos que anda sozinha a cada 5s e volta ao início no fim. */
const TestimonialRow: React.FC<{ items: typeof TESTIMONIALS; direction: 'left' | 'right' }> = ({ items, direction }) => {
  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: true, align: 'start', dragFree: false });
  const reduceMotion = useReducedMotion();
  const pausedRef = useRef(false);

  useEffect(() => {
    if (!emblaApi || reduceMotion) return;
    const id = window.setInterval(() => {
      if (pausedRef.current || document.hidden) return;
      // "right": os cards andam para a direita (volta um); "left": andam para a esquerda (avança um)
      if (direction === 'right') emblaApi.scrollPrev();
      else emblaApi.scrollNext();
    }, 5000);
    return () => window.clearInterval(id);
  }, [emblaApi, direction, reduceMotion]);

  const pause = useCallback((value: boolean) => { pausedRef.current = value; }, []);

  return (
    <div
      className="overflow-hidden"
      ref={emblaRef}
      onMouseEnter={() => pause(true)}
      onMouseLeave={() => pause(false)}
      onPointerDown={() => pause(true)}
      onPointerUp={() => window.setTimeout(() => pause(false), 5000)}
    >
      <div className="-ml-4 flex touch-pan-y">
        {items.map((t) => (
          <div key={t.id} className="min-w-0 shrink-0 grow-0 basis-[72%] pl-4 sm:basis-[40%] lg:basis-[26%]">
            <figure className="overflow-hidden rounded-2xl border border-gray-100 bg-white shadow-md">
              <img
                src={`/depoimentos-selecionados/${t.id}.jpg`}
                alt={`Depoimento: ${t.caption}`}
                className="h-80 w-full object-cover object-top sm:h-96"
                loading="lazy"
                draggable={false}
              />
              <figcaption className="border-t border-gray-100 px-4 py-3 text-sm font-medium text-gray-700">{t.caption}</figcaption>
            </figure>
          </div>
        ))}
      </div>
    </div>
  );
};

/** Número que sobe do zero quando entra na tela. */
const CountUp: React.FC<{ value: number; prefix?: string; suffix?: string }> = ({ value, prefix, suffix }) => {
  const ref = useRef<HTMLSpanElement>(null);
  const inView = useInView(ref, { once: true, margin: '-60px' });
  return (
    <span ref={ref}>
      <NumberFlow
        value={inView ? value : 0}
        locales="pt-BR"
        prefix={prefix}
        suffix={suffix}
        transformTiming={{ duration: 1200, easing: 'cubic-bezier(0.23, 1, 0.32, 1)' }}
        spinTiming={{ duration: 1200, easing: 'cubic-bezier(0.23, 1, 0.32, 1)' }}
      />
    </span>
  );
};

/** Aula gratuita: capa leve, o player do YouTube só carrega no clique. */
const FREE_LESSON = {
  videoId: '8oWaaDPoRis',
  cover: '/lovable-uploads/428bdeea-f79c-45d3-b7d7-7a6ec849ddfa.png',
  title: 'Como ajustar a pega e eliminar a dor',
  points: [
    'Por que a pega errada é a causa mais comum da dor',
    'O que ajustar na próxima mamada',
    'Como são as aulas do curso, por dentro',
  ],
};

const FreeLesson: React.FC<{ onCta: () => void }> = ({ onCta }) => {
  const [playing, setPlaying] = useState(false);

  const play = () => {
    setPlaying(true);
    try {
      (window as any).fbq?.('track', 'ViewContent', { content_name: 'Aula gratuita' });
    } catch {
      // pixel indisponível
    }
  };

  return (
    <section id="aula-gratuita" className="scroll-mt-20 px-4 py-16 sm:py-24">
      <div className="mx-auto grid max-w-6xl items-center gap-10 lg:grid-cols-12 lg:gap-14">
        <div className="order-2 lg:order-1 lg:col-span-5">
          <h2 className="text-3xl font-extrabold leading-tight text-[#2a1744] sm:text-4xl">
            Assista a uma aula do curso, de graça
          </h2>
          <p className="mt-4 text-lg leading-relaxed text-gray-600">
            Nesta aula, a Rosane mostra {FREE_LESSON.title.toLowerCase()}. É a mesma didática das mais de 100 aulas do Amamentação na Prática, gravadas com mães e bebês reais.
          </p>
          <ul className="mt-6 space-y-3">
            {FREE_LESSON.points.map((pt) => (
              <li key={pt} className="flex items-start gap-3">
                <span className="mt-0.5 flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full bg-[#5f448f]">
                  <Check className="h-3 w-3 text-white" strokeWidth={3} />
                </span>
                <span className="text-gray-800">{pt}</span>
              </li>
            ))}
          </ul>
          <button
            onClick={onCta}
            className="mt-8 inline-flex items-center gap-1.5 rounded py-1 font-bold text-[#8e3a87] underline-offset-4 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#8e3a87]/40"
          >
            Quero o curso completo <ArrowRight className="h-4 w-4" />
          </button>
        </div>

        <div className="order-1 lg:order-2 lg:col-span-7">
          <div className="relative aspect-video overflow-hidden rounded-3xl bg-[#1f1035] shadow-[0_30px_70px_rgba(42,23,68,0.3)] ring-1 ring-[#5f448f]/10">
            {playing ? (
              <iframe
                src={`https://www.youtube-nocookie.com/embed/${FREE_LESSON.videoId}?autoplay=1&rel=0&modestbranding=1`}
                title={`Aula gratuita: ${FREE_LESSON.title}`}
                className="absolute inset-0 h-full w-full"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            ) : (
              <button
                onClick={play}
                aria-label={`Assistir à aula gratuita: ${FREE_LESSON.title}`}
                className="group absolute inset-0 h-full w-full focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-[#a54d9e]/40"
              >
                <img
                  src={FREE_LESSON.cover}
                  alt=""
                  width={1600}
                  height={900}
                  loading="lazy"
                  className="h-full w-full object-cover transition-transform duration-500 ease-out group-hover:scale-[1.02]"
                />
                <span className="absolute inset-0 bg-gradient-to-t from-[#1f1035]/80 via-[#1f1035]/10 to-transparent" />
                <span className="absolute left-1/2 top-1/2 flex h-20 w-20 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full border-4 border-white bg-[#a54d9e] shadow-[0_10px_30px_rgba(0,0,0,0.35)] transition-transform duration-200 ease-out group-hover:scale-105 group-active:scale-95 sm:h-24 sm:w-24">
                  <Play className="ml-1.5 h-9 w-9 text-white sm:h-11 sm:w-11" fill="currentColor" />
                </span>
                <span className="absolute bottom-5 left-5 right-5 hidden text-left sm:block">
                  <span className="block text-sm font-semibold text-white/80">Aula gratuita</span>
                  <span className="block text-lg font-extrabold text-white sm:text-xl">{FREE_LESSON.title}</span>
                </span>
              </button>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

/** Botão do topo: alterna a promessa a cada 4s, com largura fixa para não pular. */
const RotatingCta: React.FC<{ onClick: () => void }> = ({ onClick }) => {
  const [i, setI] = useState(0);
  const [paused, setPaused] = useState(false);

  useEffect(() => {
    if (paused) return;
    const id = window.setInterval(() => setI((v) => (v + 1) % HERO_CTAS.length), 4000);
    return () => window.clearInterval(id);
  }, [paused]);

  return (
    <button
      onClick={onClick}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onFocus={() => setPaused(true)}
      onBlur={() => setPaused(false)}
      className={`${primaryButton.replace('px-8', 'px-6 sm:px-8').replace('text-lg', 'text-base sm:text-lg')} w-full sm:w-auto`}
    >
      <span className="grid overflow-hidden">
        {/* reserva a largura do texto mais longo */}
        {HERO_CTAS.map((t) => (
          <span key={t} aria-hidden="true" className="invisible col-start-1 row-start-1 whitespace-nowrap">{t}</span>
        ))}
        <AnimatePresence mode="popLayout" initial={false}>
          <motion.span
            key={HERO_CTAS[i]}
            initial={{ y: '100%', opacity: 0, filter: 'blur(2px)' }}
            animate={{ y: '0%', opacity: 1, filter: 'blur(0px)' }}
            exit={{ y: '-100%', opacity: 0, filter: 'blur(2px)' }}
            transition={{ duration: 0.35, ease: EASE_OUT }}
            className="col-start-1 row-start-1 whitespace-nowrap text-center"
          >
            {HERO_CTAS[i]}
          </motion.span>
        </AnimatePresence>
      </span>
      <ArrowRight className="h-5 w-5 flex-shrink-0 transition-transform duration-150 group-hover:translate-x-0.5" />
    </button>
  );
};

/**
 * Abas que avançam sozinhas a cada 5s enquanto a seção está na tela.
 * Depois que a pessoa escolhe uma aba, o avanço automático para de vez.
 */
const AUTO_TAB_MS = 5000;
const useAutoTabs = (length: number) => {
  const ref = useRef<HTMLElement>(null);
  const inView = useInView(ref, { amount: 0.35 });
  const [index, setIndex] = useState(0);
  const [auto, setAuto] = useState(true);

  useEffect(() => {
    if (!auto || !inView) return;
    const id = window.setInterval(() => setIndex((i) => (i + 1) % length), AUTO_TAB_MS);
    return () => window.clearInterval(id);
  }, [auto, inView, length]);

  const select = (i: number) => {
    setAuto(false);
    setIndex(i);
  };

  return { ref, index, select, running: auto && inView };
};

/** Barrinha que enche durante os 5s até a próxima aba. */
const AutoProgress: React.FC<{ running: boolean; className: string }> = ({ running, className }) =>
  running ? (
    <motion.span
      aria-hidden="true"
      className={`pointer-events-none absolute origin-left rounded-full ${className}`}
      initial={{ scaleX: 0 }}
      animate={{ scaleX: 1 }}
      transition={{ duration: AUTO_TAB_MS / 1000, ease: 'linear' }}
    />
  ) : null;

/** Contagem regressiva até a data da campanha (atualiza a cada segundo). */
const useCountdown = (endsAt?: string) => {
  const end = endsAt ? new Date(endsAt).getTime() : 0;
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    if (!end) return;
    const id = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, [end]);

  const diff = Math.max(0, end - now);
  return {
    done: !!end && diff === 0,
    units: [
      { v: Math.floor(diff / 86_400_000), l: 'dias', s: 'd' },
      { v: Math.floor(diff / 3_600_000) % 24, l: 'horas', s: 'h' },
      { v: Math.floor(diff / 60_000) % 60, l: 'min', s: 'm' },
      { v: Math.floor(diff / 1000) % 60, l: 'seg', s: 's' },
    ],
  };
};

const formatDeadline = (iso: string) =>
  new Date(iso).toLocaleString('pt-BR', { day: 'numeric', month: 'long', hour: '2-digit', minute: '2-digit', timeZone: 'America/Sao_Paulo' });

/** Tarja preta da Black com contagem regressiva; fica presa no topo junto com o menu. */
const BlackBar: React.FC<{ campaign: Campaign; onClick: () => void }> = ({ campaign, onClick }) => {
  const { done, units } = useCountdown(campaign.endsAt);
  const [title, pill] = (campaign.badge ?? 'Black November').split(' · ');

  return (
    <button
      onClick={onClick}
      aria-label={`${title}: ver a oferta`}
      className="block w-full bg-black px-4 py-2 text-white transition-colors hover:bg-[#111] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[#f0b6e8]"
    >
      <span className="mx-auto flex max-w-6xl items-center justify-center gap-2.5 whitespace-nowrap sm:gap-6">
        <span className="flex items-center gap-2">
          <span className="text-xs font-black uppercase tracking-[0.08em] sm:text-sm sm:tracking-[0.16em]">{title}</span>
          {pill && (
            <span className="hidden rounded bg-[#f0b6e8] px-1.5 py-0.5 text-[11px] font-extrabold uppercase text-black min-[400px]:inline sm:text-xs">{pill}</span>
          )}
        </span>
        {done ? (
          <span className="text-xs font-semibold text-white/80 sm:text-sm">Oferta encerrada</span>
        ) : (
          <span className="flex items-center gap-1 sm:gap-2" aria-label="Tempo até o fim da oferta">
            <span className="hidden text-sm text-white/70 md:inline">Termina em</span>
            {units.map((u) => (
              <span key={u.l} className="flex items-baseline rounded-md bg-white/10 px-1.5 py-0.5 tabular-nums">
                <span className="text-sm font-extrabold sm:text-base">
                  <NumberFlow value={u.v} format={{ minimumIntegerDigits: 2 }} />
                </span>
                <span className="ml-0.5 text-[10px] font-semibold text-white/60 sm:hidden">{u.s}</span>
                <span className="ml-1 hidden text-[11px] font-semibold text-white/60 sm:inline">{u.l}</span>
              </span>
            ))}
          </span>
        )}
      </span>
    </button>
  );
};

// ─── Página ────────────────────────────────────────────────────────────────

const HomeLP: React.FC<{ campaign?: Campaign }> = ({ campaign = HOME_CAMPAIGN }) => {
  const [selectedPlan, setSelectedPlan] = useState<CheckoutPlan | null>(null);
  const ppsTabs = useAutoTabs(PPS_STEPS.length);
  const momentTabs = useAutoTabs(MOMENTS.length);
  const step = ppsTabs.index;
  const moment = momentTabs.index;
  const [showStickyCta, setShowStickyCta] = useState(false);
  const [coursesOpen, setCoursesOpen] = useState(false);
  const offerRef = useRef<HTMLElement>(null);

  useEffect(() => {
    captureFirstTouch();
    fetchGeo();
  }, []);

  // Páginas de campanha: título próprio e fora do Google (a home é a página principal)
  useEffect(() => {
    const prevTitle = document.title;
    if (campaign.pageTitle) document.title = campaign.pageTitle;
    let robots: HTMLMetaElement | null = null;
    if (campaign.noindex) {
      robots = document.createElement('meta');
      robots.name = 'robots';
      robots.content = 'noindex, nofollow';
      document.head.appendChild(robots);
    }
    return () => {
      document.title = prevTitle;
      robots?.remove();
    };
  }, [campaign]);

  // FAQ estruturado para o Google e para buscadores de IA (só na home)
  useEffect(() => {
    if (campaign.noindex) return;
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.id = 'faq-jsonld';
    script.text = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: FAQ_GROUPS.flatMap((g) => g.items).map((f) => ({
        '@type': 'Question',
        name: f.q,
        acceptedAnswer: { '@type': 'Answer', text: f.a },
      })),
    });
    document.head.appendChild(script);
    return () => script.remove();
  }, [campaign.noindex]);

  // CTA fixo no mobile: aparece depois do topo e some quando a oferta está na tela
  useEffect(() => {
    let offerVisible = false;
    const update = () => {
      setShowStickyCta(window.scrollY > 600 && !offerVisible);
      setCoursesOpen(false);
    };
    const observer = new IntersectionObserver(([entry]) => {
      offerVisible = entry.isIntersecting;
      update();
    }, { threshold: 0.1 });
    if (offerRef.current) observer.observe(offerRef.current);
    window.addEventListener('scroll', update, { passive: true });
    return () => {
      observer.disconnect();
      window.removeEventListener('scroll', update);
    };
  }, []);

  const current = PPS_STEPS[step];

  const COURSES = [
    { name: DORES_E_LESOES.name, desc: 'Alívio imediato para dor e fissuras', action: () => setSelectedPlan(DORES_E_LESOES) },
    { name: AMAMENTACAO_NA_PRATICA.name, desc: 'O Método PPS completo, para mães e gestantes', action: () => scrollTo('oferta') },
    { name: AMAMENTACAO_DE_OURO.name, desc: 'Formação para profissionais, com certificado', action: () => { window.location.href = '/profissional'; } },
  ];

  return (
    <MotionConfig reducedMotion="user">
    <div className="min-h-screen bg-[#faf7fc] font-sans text-gray-900 caret-[#8e3a87] selection:bg-[#e7a3df] selection:text-[#2a1744] [&_h1]:[text-wrap:balance] [&_h2]:[text-wrap:balance] [&_p]:[text-wrap:pretty]">

      {campaign.banner && !campaign.endsAt && (
        <div className="bg-[#a54d9e] px-4 py-2.5 text-center text-sm font-bold text-white">{campaign.banner}</div>
      )}

      {/* ── Menu ─────────────────────────────────────────────────────── */}
      <header className="sticky top-0 z-40 border-b border-white/10 bg-[#1f1035]/90 backdrop-blur-md">
        {campaign.endsAt && <BlackBar campaign={campaign} onClick={() => scrollTo('oferta')} />}
        <nav aria-label="Principal" className="mx-auto flex h-[72px] max-w-6xl items-center justify-between gap-4 px-4 sm:h-20">
          <a href="/" aria-label="Método PPS, página inicial" className="flex-shrink-0">
            <img
              src="/lovable-uploads/1de2e26f-4255-4f2e-8dc7-94f917c709eb.png"
              alt="Método PPS"
              className="h-14 brightness-0 invert sm:h-16"
            />
          </a>

          <div className="hidden items-center gap-1 lg:flex">
            <DropdownMenu modal={false} open={coursesOpen} onOpenChange={setCoursesOpen}>
              <DropdownMenuTrigger className="group inline-flex items-center gap-1 rounded-lg px-3 py-2 text-[15px] font-semibold text-white/85 outline-none transition-colors hover:text-white focus-visible:ring-2 focus-visible:ring-white/40 data-[state=open]:text-white">
                Cursos
                <ChevronDown className="h-4 w-4 transition-transform duration-200 group-data-[state=open]:rotate-180" />
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" sideOffset={10} className="z-[60] w-80 rounded-2xl border-[#5f448f]/10 bg-white p-2 text-gray-900 shadow-[0_20px_50px_rgba(42,23,68,0.25)]">
                {COURSES.map((c) => (
                  <DropdownMenuItem key={c.name} onSelect={c.action} className="flex cursor-pointer flex-col items-start gap-0.5 rounded-xl px-3 py-2.5 focus:bg-[#faf7fc]">
                    <span className="font-bold text-[#2a1744]">{c.name}</span>
                    <span className="text-sm text-gray-600">{c.desc}</span>
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
            {NAV_LINKS.map((l) => (
              <a key={l.href} href={l.href} className="rounded-lg px-3 py-2 text-[15px] font-semibold text-white/85 transition-colors hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/40">
                {l.label}
              </a>
            ))}
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={() => scrollTo('oferta')}
              className="hidden rounded-xl bg-[#a54d9e] px-5 py-2.5 text-sm font-extrabold text-white transition-[background-color,transform] duration-150 hover:bg-[#b456ad] active:scale-[0.97] sm:inline-flex"
            >
              Quero começar
            </button>
            <Sheet>
              <SheetTrigger aria-label="Abrir menu" className="rounded-lg p-2 text-white transition-colors hover:bg-white/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/40 lg:hidden">
                <Menu className="h-6 w-6" />
              </SheetTrigger>
              <SheetContent side="right" className="z-[60] w-[86%] max-w-sm overflow-y-auto bg-white p-6 text-gray-900">
                <SheetTitle className="mb-6 text-left text-lg font-extrabold text-[#2a1744]">Menu</SheetTitle>
                <p className="mb-2 text-sm font-bold text-[#8e3a87]">Cursos</p>
                <ul className="mb-6">
                  {COURSES.map((c) => (
                    <li key={c.name} className="border-b border-gray-100">
                      <SheetClose asChild>
                        <button onClick={c.action} className="w-full py-3 text-left">
                          <span className="block font-bold text-[#2a1744]">{c.name}</span>
                          <span className="text-sm text-gray-600">{c.desc}</span>
                        </button>
                      </SheetClose>
                    </li>
                  ))}
                </ul>
                <ul className="space-y-1">
                  {NAV_LINKS.map((l) => (
                    <li key={l.href}>
                      <SheetClose asChild>
                        <a href={l.href} className="block rounded-lg py-2.5 text-base font-semibold text-[#2a1744]">{l.label}</a>
                      </SheetClose>
                    </li>
                  ))}
                </ul>
                <SheetClose asChild>
                  <button onClick={() => scrollTo('oferta')} className={`${primaryButton} mt-6 w-full text-base`}>
                    Quero começar
                  </button>
                </SheetClose>
              </SheetContent>
            </Sheet>
          </div>
        </nav>
      </header>

      <main>
      {/* ── Topo ─────────────────────────────────────────────────────── */}
      <section className="relative overflow-hidden bg-gradient-to-b from-[#1f1035] via-[#2a1744] to-[#4a3371] px-4 pt-10 pb-28 sm:pt-14 sm:pb-36">
        <div className="pointer-events-none absolute -top-24 -right-24 h-96 w-96 rounded-full bg-[#c46bbb]/25 blur-3xl" />
        <div className="pointer-events-none absolute -bottom-32 -left-24 h-[28rem] w-[28rem] rounded-full bg-[#53c0c2]/15 blur-3xl" />
        <svg
          className="pointer-events-none absolute inset-x-0 bottom-0 h-16 w-full sm:h-28"
          viewBox="0 0 1440 120"
          preserveAspectRatio="none"
          aria-hidden="true"
        >
          <path fill="#faf7fc" d="M0,120 L0,70 C360,10 1080,10 1440,70 L1440,120 Z" />
        </svg>

        <div className="relative z-10 mx-auto grid max-w-6xl items-center gap-10 lg:grid-cols-12 lg:gap-12">
          <div className="text-center lg:col-span-7 lg:text-left">
            <motion.h1
              initial={{ y: 12 }}
              animate={{ y: 0 }}
              transition={{ duration: 0.7, ease: EASE_OUT }}
              className="mb-5 text-[32px] font-extrabold leading-[1.1] tracking-[-0.02em] text-white sm:text-5xl"
            >
              3 passos simples para amamentar <span className="text-[#f0b6e8]">sem{'\u00a0'}dor e com muito{'\u00a0'}leite</span>
            </motion.h1>
            <p className="mx-auto mb-8 max-w-xl text-lg leading-relaxed text-white/80 lg:mx-0">
              Seja para chegar preparada ao parto ou para resolver o que está acontecendo agora, o Método PPS mostra o que observar em cada mamada. Há {ROSANE_STATS.anos} anos a Rosane ajuda mães a amamentar com segurança.
            </p>
            <div className="flex flex-col items-center gap-5 sm:flex-row sm:justify-center lg:justify-start">
              <RotatingCta onClick={() => scrollTo('oferta')} />
            </div>
            <p className="mt-8 text-sm font-medium text-white/70">
              {ROSANE_STATS.duplas} duplas mãe e bebê · {ROSANE_STATS.anos} anos de experiência · Consultora IBCLC
            </p>
          </div>

          <div className="mx-auto w-full max-w-sm lg:col-span-5 lg:max-w-none">
            <div className="relative rounded-[28px] bg-gradient-to-br from-[#e7a3df] via-[#c46bbb] to-[#5f448f] p-[3px] shadow-[0_30px_70px_rgba(15,5,30,0.45)]">
              <img
                src="/lovable-uploads/e4b1575d-1cfa-44be-a5dc-60500d1054b7.png"
                alt="Rosane Baldissera, consultora em amamentação, sorrindo com um bebê no colo"
                width={667}
                height={1000}
                className="aspect-[4/5] w-full rounded-[25px] object-cover object-top"
                loading="eager"
              />
            </div>
          </div>
        </div>
      </section>

      {/* ── Momentos (para quem é) ───────────────────────────────────── */}
      <section ref={momentTabs.ref} className="px-4 py-14 sm:py-20">
        <div className="mx-auto max-w-5xl">
          <div className="mx-auto mb-8 max-w-2xl text-center">
            <h2 className="text-3xl font-extrabold text-[#2a1744] sm:text-4xl">É para você, em qualquer momento</h2>
            <p className="mt-3 text-lg text-gray-600">Escolha o que mais parece com você agora.</p>
          </div>

          <div role="tablist" aria-label="Seu momento" className="mx-auto mb-8 flex max-w-3xl flex-col gap-2 rounded-2xl bg-[#f3e8f7] p-1.5 sm:flex-row">
            {MOMENTS.map((m, i) => (
              <button
                key={m.key}
                role="tab"
                id={`momento-${m.key}`}
                aria-selected={moment === i}
                aria-controls="momento-panel"
                onClick={() => momentTabs.select(i)}
                className="relative flex-1 rounded-xl px-4 py-3 text-[15px] font-bold transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#5f448f]/40"
              >
                {moment === i && (
                  <motion.span
                    layoutId="momento-tab"
                    className="absolute inset-0 rounded-xl bg-[#5f448f] shadow-[0_8px_20px_rgba(95,68,143,0.3)]"
                    transition={{ type: 'spring', duration: 0.4, bounce: 0.15 }}
                  />
                )}
                <span className={`relative ${moment === i ? 'text-white' : 'text-[#5f448f]'}`}>{m.label}</span>
                {moment === i && (
                  <AutoProgress key={`mp-${i}`} running={momentTabs.running} className="bottom-1 left-4 right-4 h-0.5 bg-white/60" />
                )}
              </button>
            ))}
          </div>

          <div id="momento-panel" role="tabpanel" aria-labelledby={`momento-${MOMENTS[moment].key}`} className="mx-auto max-w-3xl">
            <AnimatePresence mode="wait" initial={false}>
              <motion.div
                key={MOMENTS[moment].key}
                initial={{ opacity: 0, y: 6, filter: 'blur(2px)' }}
                animate={{ opacity: 1, y: 0, filter: 'blur(0px)' }}
                exit={{ opacity: 0, y: -4, filter: 'blur(2px)' }}
                transition={{ duration: 0.2, ease: EASE_OUT }}
                className="grid gap-6 md:grid-cols-2 md:gap-10"
              >
                <div>
                  <h3 className="text-2xl font-extrabold text-[#2a1744]">{MOMENTS[moment].title}</h3>
                  <p className="mt-3 text-lg leading-relaxed text-gray-600">{MOMENTS[moment].text}</p>
                  <button
                    onClick={() => scrollTo('conteudo')}
                    className="mt-5 inline-flex items-center gap-1.5 rounded py-1 font-bold text-[#8e3a87] underline-offset-4 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#8e3a87]/40"
                  >
                    Ver o que você aprende <ArrowRight className="h-4 w-4" />
                  </button>
                </div>
                <div>
                  <p className="mb-2 text-sm font-bold text-[#8e3a87]">É para você se...</p>
                  <ul>
                    {MOMENTS[moment].items.map((item) => (
                      <li key={item} className="flex items-start gap-3 border-b border-[#5f448f]/10 py-3.5 last:border-0">
                        <span className="mt-0.5 flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-[#a54d9e]">
                          <Check className="h-3.5 w-3.5 text-white" strokeWidth={3} />
                        </span>
                        <span className="text-[17px] leading-snug text-gray-800">{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </motion.div>
            </AnimatePresence>
          </div>
        </div>
      </section>

      {/* ── Método PPS ───────────────────────────────────────────────── */}
      <section id="metodo" ref={ppsTabs.ref} className="scroll-mt-20 bg-white px-4 py-16 sm:py-24">
        <div className="mx-auto max-w-6xl">
          <div className="mx-auto mb-10 max-w-2xl text-center">
            <h2 className="text-3xl font-extrabold text-[#2a1744] sm:text-4xl">Três letras que mudam cada mamada</h2>
            <p className="mt-4 text-lg text-gray-600">
              Posição, Pega e Sucção. Uma etapa depende da outra, e quando você aprende a olhar as três juntas, a mamada deixa de ser um mistério.
            </p>
          </div>

          <div className="grid items-start gap-8 lg:grid-cols-2 lg:gap-14">
            <div className="order-2 lg:order-1">
              <div role="tablist" aria-label="Etapas do Método PPS" className="mb-7 flex gap-3">
                {PPS_STEPS.map((s, i) => (
                  <button
                    key={s.key}
                    role="tab"
                    id={`tab-${s.key}`}
                    aria-selected={step === i}
                    aria-controls="pps-panel"
                    aria-label={s.name}
                    title={s.name}
                    onClick={() => ppsTabs.select(i)}
                    className={`relative flex h-16 w-16 items-center justify-center overflow-hidden rounded-2xl text-3xl font-extrabold transition-[background-color,color,transform,box-shadow] duration-200 ease-out active:scale-[0.95] focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-[#5f448f]/30 sm:h-20 sm:w-20 sm:text-4xl ${
                      step === i
                        ? 'bg-[#5f448f] text-white shadow-[0_10px_24px_rgba(95,68,143,0.35)]'
                        : 'border-2 border-[#5f448f]/25 bg-white text-[#5f448f] hover:border-[#5f448f]/60'
                    }`}
                  >
                    {s.letter}
                    {step === i && (
                      <AutoProgress key={`pp-${i}`} running={ppsTabs.running} className="bottom-1.5 left-3 right-3 h-1 bg-white/60" />
                    )}
                  </button>
                ))}
              </div>

              <div id="pps-panel" role="tabpanel" aria-labelledby={`tab-${current.key}`} className="min-h-[19rem]">
                <AnimatePresence mode="wait" initial={false}>
                  <motion.div
                    key={current.key}
                    initial={{ opacity: 0, y: 6, filter: 'blur(2px)' }}
                    animate={{ opacity: 1, y: 0, filter: 'blur(0px)' }}
                    exit={{ opacity: 0, y: -4, filter: 'blur(2px)' }}
                    transition={{ duration: 0.2, ease: EASE_OUT }}
                  >
                    <h3 className="mb-3 text-2xl font-extrabold text-[#2a1744]">{current.name}</h3>
                    <p className="text-lg leading-relaxed text-gray-600">{current.text}</p>
                    <p className="mt-6 mb-3 text-sm font-bold text-[#8e3a87]">O que observar</p>
                    <ul className="space-y-2.5">
                      {current.observe.map((o) => (
                        <li key={o} className="flex items-start gap-3">
                          <span className="mt-0.5 flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full bg-[#5f448f]">
                            <Check className="h-3 w-3 text-white" strokeWidth={3} />
                          </span>
                          <span className="text-gray-800">{o}</span>
                        </li>
                      ))}
                    </ul>
                  </motion.div>
                </AnimatePresence>
              </div>

              <div className="mt-6 flex items-center gap-2" aria-hidden="true">
                {PPS_STEPS.map((s, i) => (
                  <div key={s.key} className="h-1.5 flex-1 overflow-hidden rounded-full bg-[#f3e8f7]">
                    <motion.div
                      className="h-full rounded-full bg-[#a54d9e]"
                      initial={false}
                      animate={{ scaleX: i <= step ? 1 : 0 }}
                      style={{ originX: 0 }}
                      transition={{ duration: 0.3, ease: EASE_OUT }}
                    />
                  </div>
                ))}
              </div>
              {step < PPS_STEPS.length - 1 && (
                <button
                  onClick={() => ppsTabs.select(step + 1)}
                  className="mt-5 inline-flex items-center gap-1.5 rounded py-1 text-sm font-semibold text-[#8e3a87] underline-offset-4 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#8e3a87]/40"
                >
                  Próxima etapa: {PPS_STEPS[step + 1].name} <ArrowRight className="h-4 w-4" />
                </button>
              )}
            </div>

            <div className="relative order-1 aspect-[4/3] overflow-hidden rounded-3xl bg-[#f3e8f7] shadow-xl lg:order-2 lg:sticky lg:top-28">
              <AnimatePresence initial={false}>
                <motion.img
                  key={current.image}
                  src={current.image}
                  alt={current.alt}
                  initial={{ opacity: 0, scale: 1.03 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.35, ease: EASE_OUT }}
                  className="absolute inset-0 h-full w-full object-cover"
                />
              </AnimatePresence>
            </div>
          </div>
        </div>
      </section>

      {/* ── Depoimentos ──────────────────────────────────────────────── */}
      <section className="overflow-hidden py-16 sm:py-24">
        <div className="mx-auto mb-10 max-w-6xl px-4 text-center">
          <h2 className="text-3xl font-extrabold text-[#2a1744] sm:text-4xl">Quem aplicou, conta</h2>
          <p className="mt-3 text-lg text-gray-600">Relatos reais de mães que usaram o Método PPS.</p>
        </div>
        <div className="space-y-5 px-4">
          <TestimonialRow items={ROW_TOP} direction="right" />
          <TestimonialRow items={ROW_BOTTOM} direction="left" />
        </div>
      </section>

      {/* ── O que você aprende ───────────────────────────────────────── */}
      <section id="conteudo" className="scroll-mt-20 bg-white px-4 py-16 sm:py-24">
        <div className="mx-auto max-w-5xl">
          <div className="mx-auto mb-12 max-w-2xl text-center">
            <h2 className="text-3xl font-extrabold text-[#2a1744] sm:text-4xl">O que você aprende</h2>
            <p className="mt-4 text-lg text-gray-600">
              10 módulos e mais de 100 aulas, do preparo na gravidez até o sono do bebê. Toque em um módulo para ver o que tem dentro.
            </p>
          </div>

          <div className="space-y-12">
            {PHASES.map((phase) => (
              <div key={phase.name} className="grid gap-4 md:grid-cols-[180px_1fr] md:gap-10">
                <div className="md:pt-4">
                  <h3 className="text-2xl font-extrabold text-[#5f448f]">{phase.name}</h3>
                  <p className="text-sm font-medium text-gray-600">{phase.range}</p>
                </div>
                <Accordion type="multiple" className="rounded-2xl border border-[#5f448f]/10 bg-[#faf7fc] px-4 sm:px-6">
                  {phase.modules.map((m) => (
                    <AccordionItem key={m.n} value={`m-${m.n}`} className="border-[#5f448f]/10 last:border-0">
                      <AccordionTrigger className="gap-4 py-5 text-left hover:no-underline [&>svg]:text-[#8e3a87]">
                        <span className="flex items-start gap-4">
                          <span className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-[#5f448f] text-base font-extrabold tabular-nums text-white">
                            {m.n}
                          </span>
                          <span>
                            <span className="block text-base font-bold text-[#2a1744] sm:text-lg">{m.title}</span>
                            <span className="mt-0.5 block text-[15px] font-normal leading-snug text-gray-600">{m.desc}</span>
                          </span>
                        </span>
                      </AccordionTrigger>
                      <AccordionContent className="pb-5 pl-14">
                        <ul className="grid gap-x-6 gap-y-2 sm:grid-cols-2">
                          {m.topics.map((t) => (
                            <li key={t} className="flex items-start gap-2 text-[15px] text-gray-700">
                              <span className="mt-2 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-[#a54d9e]" />
                              {t}
                            </li>
                          ))}
                        </ul>
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Aula gratuita ────────────────────────────────────────────── */}
      <FreeLesson onCta={() => scrollTo('oferta')} />

      {/* ── Ciência ──────────────────────────────────────────────────── */}
      <section className="bg-[#2a1744] px-4 py-16 text-white sm:py-24">
        <div className="mx-auto max-w-6xl">
          <div className="mx-auto mb-12 max-w-2xl text-center">
            <h2 className="text-3xl font-extrabold sm:text-4xl">O que a ciência já mostrou</h2>
            <p className="mt-4 text-lg text-white/75">Estudos publicados que explicam por que a técnica faz tanta diferença.</p>
          </div>

          <div className="mx-auto mb-12 grid max-w-4xl items-center gap-6 border-y border-white/15 py-8 md:grid-cols-[auto_1fr] md:gap-12">
            <div className="text-center md:text-left">
              <div className="text-7xl font-extrabold tracking-[-0.04em] text-[#f0b6e8] sm:text-8xl">45,8%</div>
              <a
                href="https://enani.nutricao.ufrj.br/"
                target="_blank"
                rel="noopener noreferrer"
                className="mt-2 inline-block text-sm font-medium text-white/70 underline-offset-4 hover:underline"
              >
                ENANI 2019, UFRJ e Ministério da Saúde
              </a>
            </div>
            <div>
              <h3 className="mb-3 text-2xl font-extrabold leading-tight">Menos da metade dos bebês chega aos 6 meses mamando só no peito.</h3>
              <p className="text-lg leading-relaxed text-white/80">
                Dor, pouco leite, bebê que não pega, insegurança nos primeiros dias. Na maioria das vezes, a raiz está na técnica, e técnica se aprende, de preferência ainda na gravidez.
              </p>
            </div>
          </div>
          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {NEWS.map((n) => (
              <article key={n.url} className="group flex flex-col rounded-2xl bg-white p-6 text-gray-900 shadow-[0_12px_30px_rgba(0,0,0,0.25)] transition-transform duration-200 ease-out hover:-translate-y-1">
                <div className="mb-4 flex items-center justify-between gap-3 border-b border-gray-100 pb-3">
                  <span className="text-xs font-extrabold uppercase tracking-wide text-[#5f448f]">{n.source}</span>
                  <span className="text-xs font-medium tabular-nums text-gray-500">{n.year}</span>
                </div>
                <span className="mb-2 inline-flex w-fit rounded-full bg-[#f3e8f7] px-2.5 py-0.5 text-xs font-bold text-[#5c2457]">{n.tag}</span>
                <h3 className="mb-3 text-xl font-extrabold leading-snug text-[#2a1744]">{n.title}</h3>
                <p className="mb-5 flex-1 text-[15px] leading-relaxed text-gray-600">{n.summary}</p>
                <a
                  href={n.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-sm font-bold text-[#8e3a87] underline-offset-4 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#8e3a87]/40"
                >
                  Ler o estudo <ArrowUpRight className="h-4 w-4" aria-hidden="true" />
                  <span className="sr-only">(abre em nova aba)</span>
                </a>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* ── Rosane ───────────────────────────────────────────────────── */}
      <section id="rosane" className="relative scroll-mt-20 overflow-hidden px-4 py-16 sm:py-24">
        <div className="pointer-events-none absolute right-0 top-0 h-96 w-96 rounded-full bg-[#c46bbb]/10 blur-3xl" />
        <div className="relative mx-auto flex max-w-5xl flex-col items-center gap-10 md:flex-row md:gap-14">
          <div className="flex flex-shrink-0 justify-center md:w-5/12">
            <div className="relative w-72 sm:w-80 md:w-full">
              {/* Mancha lilás deslocada atrás da foto, no mesmo formato orgânico */}
              <div
                className="absolute inset-0 translate-x-4 translate-y-5 bg-gradient-to-br from-[#e7a3df] to-[#5f448f] opacity-40"
                style={{ borderRadius: '42% 58% 55% 45% / 48% 40% 60% 52%' }}
                aria-hidden="true"
              />
              <img
                src="/rosane-consultorio.jpg"
                alt="Rosane Baldissera, consultora em amamentação, de jaleco no consultório"
                width={840}
                height={1050}
                className="relative z-10 aspect-[4/5] w-full object-cover object-top shadow-[0_24px_50px_rgba(42,23,68,0.25)]"
                style={{ borderRadius: '58% 42% 46% 54% / 44% 52% 48% 56%' }}
                loading="lazy"
              />
            </div>
          </div>
          <div className="md:w-7/12">
            <h2 className="text-3xl font-extrabold text-[#2a1744] md:text-4xl">Rosane Baldissera</h2>
            <p className="mt-2 font-medium text-[#8e3a87]">Criadora do Método PPS</p>
            <p className="mt-6 text-lg leading-relaxed text-gray-600">
              Sou nutricionista materno-infantil, consultora internacional em amamentação (IBCLC) e mestre em saúde da criança pela UFRGS. Há {ROSANE_STATS.anos} anos acompanho mães nos primeiros dias de amamentação. Também sou mãe do Felipe, que mamou até os 3 anos.
            </p>
            <dl className="mt-8 grid grid-cols-2 gap-x-6 gap-y-5 sm:grid-cols-4">
              {[
                { v: <CountUp value={15000} prefix="+" />, l: 'duplas mãe e bebê' },
                { v: <CountUp value={25} suffix=" anos" />, l: 'de prática clínica' },
                { v: <CountUp value={2000} prefix="+" />, l: 'profissionais formados' },
                { v: <CountUp value={70} prefix="+" suffix=" mil" />, l: 'seguidores no Instagram' },
              ].map((s) => (
                <div key={s.l} className="border-t border-[#5f448f]/15 pt-3">
                  <dt className="text-2xl font-extrabold tabular-nums text-[#5f448f]">{s.v}</dt>
                  <dd className="mt-0.5 text-sm text-gray-600">{s.l}</dd>
                </div>
              ))}
            </dl>
          </div>
        </div>
      </section>

      {/* ── Oferta ───────────────────────────────────────────────────── */}
      <section id="oferta" ref={offerRef} className="scroll-mt-20 bg-gradient-to-b from-[#faf7fc] via-[#f3e8f7] to-[#faf7fc] px-4 py-16 sm:py-24">
        <div className="mx-auto max-w-5xl">
          <h2 className="mb-10 text-center text-3xl font-extrabold text-[#2a1744] sm:text-4xl">{campaign.offerHeading}</h2>

          <div className="grid overflow-hidden rounded-3xl bg-white shadow-[0_30px_70px_rgba(42,23,68,0.18)] ring-1 ring-[#5f448f]/10 lg:grid-cols-2">
            <div className="bg-gradient-to-b from-[#2a1744] to-[#4a3371] p-7 text-white sm:p-10">
              {campaign.badge && (
                <span className="mb-4 inline-flex rounded-full bg-black px-3 py-1 text-xs font-extrabold uppercase tracking-wide text-white ring-1 ring-white/25">
                  {campaign.badge}
                </span>
              )}
              <h3 className="text-2xl font-extrabold">{campaign.plan.name}</h3>
              <p className="mt-1 text-[#f0b6e8]">{campaign.offerTagline}</p>
              <div className="my-7 border-y border-white/10 py-6">
                <p className="text-white/60 line-through">{campaign.priceFrom}</p>
                <p className="text-5xl font-extrabold tracking-tight">{campaign.plan.price}</p>
                <p className="mt-1 text-sm text-white/70">{campaign.installments}</p>
                {campaign.endsAt && (
                  <p className="mt-3 text-sm font-semibold text-[#f0b6e8]">Válido até {formatDeadline(campaign.endsAt)}</p>
                )}
              </div>
              <ul className="mb-8 space-y-3">
                {campaign.included.map((item) => (
                  <li key={item} className="flex items-start gap-3">
                    <span className="mt-0.5 flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full bg-[#c46bbb]">
                      <Check className="h-3.5 w-3.5 text-white" strokeWidth={3} />
                    </span>
                    <span className="text-[15px] leading-snug text-white/90">{item}</span>
                  </li>
                ))}
              </ul>
              <button onClick={() => setSelectedPlan(campaign.plan)} className={`${primaryButton} w-full`}>
                Quero começar agora
                <ArrowRight className="h-5 w-5 transition-transform duration-150 group-hover:translate-x-0.5" />
              </button>
              <p className="mt-4 flex items-center justify-center gap-2 text-sm text-white/70">
                <ShieldCheck className="h-4 w-4" aria-hidden="true" /> 7 dias de garantia, reembolso integral
              </p>
            </div>

            <div className="p-7 sm:p-10">
              <div className="mb-5 flex items-center gap-2 text-[#8e3a87]">
                <Gift className="h-5 w-5" aria-hidden="true" />
                <h3 className="text-lg font-extrabold">5 bônus inclusos</h3>
              </div>
              <ul>
                {BONUSES.map((b) => (
                  <li key={b.title} className="flex items-start justify-between gap-4 border-b border-gray-100 py-4 last:border-0">
                    <div>
                      <p className="font-bold text-[#2a1744]">{b.title}</p>
                      <p className="mt-0.5 text-sm leading-snug text-gray-600">{b.desc}</p>
                    </div>
                    <div className="flex-shrink-0 text-right">
                      <p className="text-xs text-gray-500 line-through">{b.from}</p>
                      <p className="text-sm font-extrabold text-[#1a6f71]">Grátis</p>
                    </div>
                  </li>
                ))}
              </ul>
              <p className="mt-4 rounded-xl bg-[#faf7fc] px-4 py-3 text-sm text-gray-700">
                <strong className="text-[#2a1744]">R$ 605 em bônus</strong> sem custo adicional.
              </p>
            </div>
          </div>

          {campaign.bumpNote && (
            <div id="comunidade" className="mx-auto mt-8 flex max-w-3xl scroll-mt-24 items-start gap-4 rounded-2xl border-2 border-dashed border-[#a54d9e]/40 bg-white px-5 py-4">
              <span className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-[#5f448f]">
                <Users className="h-5 w-5 text-white" aria-hidden="true" />
              </span>
              <div>
                <p className="font-extrabold text-[#2a1744]">{campaign.bumpNote.title}</p>
                <p className="mt-0.5 text-[15px] leading-snug text-gray-700">{campaign.bumpNote.text}</p>
              </div>
            </div>
          )}

          <div className="mx-auto mt-10 flex max-w-2xl items-start gap-4">
            <ShieldCheck className="mt-0.5 h-8 w-8 flex-shrink-0 text-[#5f448f]" aria-hidden="true" />
            <p className="text-gray-700">
              <strong className="text-[#2a1744]">Risco zero.</strong> Você tem 7 dias para assistir às aulas e decidir. Se não for para você, é só pedir o reembolso e devolvemos o valor inteiro, sem perguntas.
            </p>
          </div>

          <ol className="mt-14 grid gap-6 sm:grid-cols-3">
            {STEPS_ACCESS.map((s, i) => (
              <li key={s.title} className="flex gap-4">
                <span className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-[#5f448f] text-sm font-extrabold text-white">{i + 1}</span>
                <div>
                  <p className="font-bold text-[#2a1744]">{s.title}</p>
                  <p className="mt-0.5 text-[15px] leading-snug text-gray-600">{s.text}</p>
                </div>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* ── Outros caminhos ──────────────────────────────────────────── */}
      <section id="outros-cursos" className="scroll-mt-20 bg-white px-4 py-16 sm:py-24">
        <div className="mx-auto max-w-5xl">
          <div className="mx-auto mb-10 max-w-2xl text-center">
            <h2 className="text-3xl font-extrabold text-[#2a1744] sm:text-4xl">Outros caminhos com a Rosane</h2>
            <p className="mt-3 text-lg text-gray-600">Para quem precisa de algo mais focado, é profissional de saúde ou quer companhia na jornada.</p>
          </div>
          <div className={`grid gap-5 ${campaign.showCommunityCard ? 'md:grid-cols-3' : 'mx-auto max-w-3xl md:grid-cols-2'}`}>
            {[
              {
                id: 'dores',
                situation: 'Só preciso resolver a dor agora',
                product: DORES_E_LESOES.name,
                desc: 'Curso focado em aliviar dor e fissuras, com correções práticas de pega e posição.',
                price: DORES_E_LESOES.price,
                cta: 'Quero esse curso',
                action: () => setSelectedPlan(DORES_E_LESOES),
                show: true,
              },
              {
                id: 'ouro',
                situation: 'Sou profissional de saúde',
                product: AMAMENTACAO_DE_OURO.name,
                desc: 'Formação completa, com certificado de aperfeiçoamento profissional, para quem atende mães e bebês.',
                price: AMAMENTACAO_DE_OURO.price,
                cta: 'Ver a formação',
                action: () => { window.location.href = '/profissional'; },
                show: true,
              },
              {
                id: 'comunidade',
                situation: 'Quero contato direto com a Rosane',
                product: COMUNIDADE.name,
                desc: 'Grupo no WhatsApp para tirar as dúvidas do dia a dia e trocar com outras mães.',
                price: COMUNIDADE.price,
                cta: 'Quero entrar',
                action: () => setSelectedPlan(COMUNIDADE),
                show: campaign.showCommunityCard,
              },
            ].filter((c) => c.show).map((c) => (
              <article
                key={c.id}
                id={c.id === 'comunidade' ? 'comunidade' : undefined}
                className="flex scroll-mt-24 flex-col rounded-2xl border border-[#5f448f]/15 bg-[#faf7fc] p-6"
              >
                <h3 className="text-xl font-extrabold leading-snug text-[#2a1744]">{c.situation}</h3>
                <p className="mt-1 text-sm font-bold text-[#8e3a87]">{c.product}</p>
                <p className="mt-3 flex-1 text-[15px] leading-relaxed text-gray-600">{c.desc}</p>
                <p className="mt-5 text-2xl font-extrabold tabular-nums text-[#2a1744]">{c.price}</p>
                <button
                  onClick={c.action}
                  className="mt-4 inline-flex items-center justify-center gap-2 rounded-xl border-2 border-[#5f448f] px-5 py-3 font-bold text-[#5f448f] transition-[background-color,color,transform] duration-150 ease-out hover:bg-[#5f448f] hover:text-white active:scale-[0.97] focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-[#5f448f]/25"
                >
                  {c.cta} <ArrowRight className="h-4 w-4" />
                </button>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* ── Perguntas ────────────────────────────────────────────────── */}
      <section id="duvidas" className="scroll-mt-20 px-4 py-16 sm:py-24">
        <div className="mx-auto max-w-3xl">
          <h2 className="mb-10 text-center text-3xl font-extrabold text-[#2a1744] sm:text-4xl">Perguntas frequentes</h2>
          <div className="space-y-10">
            {FAQ_GROUPS.map((g) => (
              <div key={g.title}>
                <h3 className="mb-2 text-lg font-extrabold text-[#5f448f]">{g.title}</h3>
                <Accordion type="single" collapsible>
                  {g.items.map((f) => (
                    <AccordionItem key={f.q} value={f.q} className="border-[#5f448f]/15">
                      <AccordionTrigger className="py-5 text-left text-base font-bold text-[#2a1744] hover:no-underline sm:text-lg [&>svg]:text-[#8e3a87]">
                        {f.q}
                      </AccordionTrigger>
                      <AccordionContent className="text-base leading-relaxed text-gray-600">{f.a}</AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              </div>
            ))}
          </div>
          <p className="mt-8 text-sm text-gray-600">
            As respostas são orientações gerais e não substituem a avaliação de um profissional de saúde.
          </p>
          <a
            href={WHATSAPP_LINK}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-6 inline-flex items-center gap-2 rounded-xl border-2 border-[#5f448f]/20 px-5 py-3 font-bold text-[#2a1744] transition-colors hover:border-[#5f448f]/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#5f448f]/40"
          >
            <MessageCircle className="h-5 w-5 text-[#1a6f71]" aria-hidden="true" /> Ainda com dúvida? Fale com o nosso time
          </a>
        </div>
      </section>

      {/* ── Fechamento ───────────────────────────────────────────────── */}
      <section className="bg-gradient-to-b from-[#2a1744] to-[#1f1035] px-4 py-16 text-center text-white sm:py-24">
        <div className="mx-auto max-w-2xl">
          <h2 className="text-3xl font-extrabold leading-tight sm:text-4xl">Da gravidez à última mamada, com segurança.</h2>
          <p className="mt-4 text-lg text-white/75">Chegue preparada para a primeira mamada ou transforme a próxima. O caminho é o mesmo: posição, pega e sucção.</p>
          <button onClick={() => setSelectedPlan(campaign.plan)} className={`${primaryButton} mt-8`}>
            {campaign.finalCta}
            <ArrowRight className="h-5 w-5 transition-transform duration-150 group-hover:translate-x-0.5" />
          </button>
        </div>
      </section>
      </main>

      <footer className="bg-[#1f1035] px-4 py-8 pb-24 text-center text-xs text-white/60 sm:pb-8">
        <p>© {new Date().getFullYear()} Amamentação na Prática · Método PPS · Rosane Baldissera</p>
        <a href={PRIVACY_LINK} className="mt-2 inline-block underline-offset-2 hover:text-white/90 hover:underline">
          Termos e Política de Privacidade
        </a>
      </footer>

      {/* ── CTA fixo (mobile) ────────────────────────────────────────── */}
      <AnimatePresence>
        {showStickyCta && !selectedPlan && (
          <motion.div
            initial={{ y: '100%' }}
            animate={{ y: 0 }}
            exit={{ y: '100%' }}
            transition={{ duration: 0.25, ease: EASE_OUT }}
            className="fixed inset-x-0 bottom-0 z-30 border-t border-[#5f448f]/10 bg-white/95 p-3 backdrop-blur sm:hidden"
          >
            <button
              onClick={() => scrollTo('oferta')}
              className="flex w-full items-center justify-center gap-2 rounded-xl bg-[#a54d9e] py-3.5 font-extrabold text-white shadow-lg transition-transform duration-150 active:scale-[0.97]"
            >
              Ver a oferta por {campaign.plan.price} <ArrowDown className="h-4 w-4" />
            </button>
          </motion.div>
        )}
      </AnimatePresence>

      <ChatWidget raised={showStickyCta} hidden={!!selectedPlan} />

      <LeadCheckoutDialog plan={selectedPlan} onOpenChange={(open) => !open && setSelectedPlan(null)} />
    </div>
    </MotionConfig>
  );
};

export default HomeLP;
